import type { ColumnDrag } from "@/hooks/use-column-drag";
import type { OpportunityWithRelations } from "@/hooks/use-opportunities";
import type { OfferWithCustomer } from "@/hooks/use-offers-queries";
import { MOVABLE_STAGES, STAGE_LABELS } from "@/lib/opportunity-calc";
import type { PipeCardData, PipeStage } from "@/lib/pipe-board-calc";
import { cn } from "@/lib/utils";
import PipeCard from "./PipeCard";

interface PipeColumnProps {
  stage: PipeStage;
  cards: PipeCardData<OpportunityWithRelations, OfferWithCustomer>[];
  drag: ColumnDrag;
  onOpen: (opportunityId: string) => void;
}

const EMPTY_HINT: Record<PipeStage, string> = {
  outreach: "Ingen prosesser i Outreach. Start en fra kundekortet eller ta ansvar for et signal.",
  meeting: "Ingen prosesser i Møte. Dra en hit fra Outreach når møtet er avtalt.",
  offer: "Ingen prosesser i Tilbud. Lag et tilbud fra en prosess for å flytte den hit.",
};

/** Én kolonne i pipen. Alle kolonner er slippsoner; Tilbud avviser (se PipeTab). */
export default function PipeColumn({ stage, cards, drag, onOpen }: PipeColumnProps) {
  const over = drag.overColumn === stage;
  return (
    <section
      aria-label={`${STAGE_LABELS[stage]} (${cards.length})`}
      {...drag.columnProps(stage)}
      className={cn("min-h-40 space-y-3 border-[1.5px] border-foreground/40 bg-muted/20 p-3", over && "border-foreground bg-muted/50")}
    >
      <h3 className="font-display text-base font-semibold">
        {STAGE_LABELS[stage]} <span className="text-muted-foreground">({cards.length})</span>
      </h3>
      {cards.length === 0 ? (
        <p className="text-sm text-muted-foreground">{EMPTY_HINT[stage]}</p>
      ) : (
        cards.map((c) => (
          <PipeCard
            key={c.opportunity.id}
            opportunity={c.opportunity}
            offer={c.offer}
            days={c.days}
            stalled={c.stalled}
            dragging={drag.draggingId === c.opportunity.id}
            dragProps={drag.cardProps(c.opportunity.id, MOVABLE_STAGES.includes(c.opportunity.stage))}
            onOpen={() => onOpen(c.opportunity.id)}
          />
        ))
      )}
    </section>
  );
}
